<template>
  <main class="login-page app-screen">
    <section class="login-card">
      <div class="login-heading">
        <p class="login-kicker">ГИС Инвентаризация</p>

        <h1>Инвентаризация кладбищ и захоронений</h1>

      </div>

      <form class="login-form" @submit.prevent="login">
        <div class="form-group">
          <label for="username" class="form-label">Логин</label>

          <input id="username" v-model="username" class="form-input" type="text" placeholder="Введите ваш логин"
            autocomplete="username" required />
        </div>

        <div class="form-group">
          <label for="password" class="form-label">Пароль</label>

          <input id="password" v-model="password" class="form-input" type="password" placeholder="Введите ваш пароль"
            autocomplete="current-password" required />
        </div>

        <button class="login-button" type="submit" :disabled="isLoading">
          <span v-if="!isLoading">Войти</span>
          <span v-else class="loading-spinner"></span>
        </button>

        <div v-if="errorMessage" class="error-message">
          <svg v-if="isOffline" class="error-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h.01"/><path d="M2 8.82a15 15 0 0 1 20 0"/><path d="M5 12.859a10 10 0 0 1 14.536 0"/><path d="M8.5 16.429a5 5 0 0 1 7 0"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
          {{ errorMessage }}
        </div>
      </form>
    </section>
  </main>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/authStore'

const authStore = useAuthStore()
const router = useRouter()

const username = ref('')
const password = ref('')
const isLoading = ref(false)
const errorMessage = ref('')
const isOffline = ref(false)

async function login() {
  if (!username.value || !password.value) {
    errorMessage.value = 'Пожалуйста, заполните все поля'
    return
  }

  try {
    if (!navigator.onLine) {
      isOffline.value = true
      errorMessage.value = 'Отсутствует подключение к интернету, проверьте соединение'
      return
    }

    isLoading.value = true
    errorMessage.value = ''
    isOffline.value = false

    await authStore.obtainToken({
      username: username.value,
      password: password.value,
    })

    await authStore.objectUser()

    router.push({ name: 'home' })
  } catch (error: any) {
    if (!navigator.onLine) {
      isOffline.value = true
      errorMessage.value = 'Отсутствует подключение к интернету, проверьте соединение'
    } else {
      errorMessage.value =
        error.response?.data?.description ||
        'Неверный логин или пароль'
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.login-page {
  display: flex;
  align-items: center;
  justify-content: center;

  padding-left: 10px;
  padding-right: 10px;

  overflow: hidden;

  background:
    radial-gradient(circle at top left, rgba(255, 95, 3, 0.12), transparent 30vw),
    linear-gradient(180deg, var(--surface) 0%, var(--surface-2) 100%);
}

.login-card {
  width: min(100%, 440px);
  max-height: calc(100% - 24px);

  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  padding: 38px;

  background: var(--surface-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  backdrop-filter: blur(18px);
}

.login-heading {
  margin-bottom: 30px;
  text-align: center;
}

.login-kicker {
  margin-bottom: 10px;

  color: var(--secondary);
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.login-heading h1 {
  margin-bottom: 12px;

  color: var(--primary);
  font-family: var(--font-display);
  font-size: 30px;
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: -0.05em;
}

.login-description {
  color: var(--text-muted);
  font-size: 15px;
  line-height: 1.6;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 800;
}

.form-input {
  width: 100%;
  min-height: 52px;
  padding: 0 16px;

  color: var(--text);
  background: rgba(255, 255, 255, 0.72);

  border: 1px solid var(--border);
  border-radius: var(--radius-sm);

  font-size: 16px;
  outline: none;

  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

:root[data-theme='dark'] .form-input {
  background: rgba(255, 255, 255, 0.05);
}

.form-input::placeholder {
  color: var(--text-soft);
}

.form-input:focus {
  border-color: var(--focus);
  box-shadow: 0 0 0 4px rgba(255, 95, 3, 0.14);
}

.login-button {
  min-height: 54px;
  margin-top: 8px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  border-radius: 999px;

  color: #fff;
  background: var(--secondary);
  box-shadow: 0 16px 34px rgba(255, 95, 3, 0.26);

  font-size: 15px;
  font-weight: 900;
  cursor: pointer;

  transition:
    transform 0.22s ease,
    background 0.22s ease,
    box-shadow 0.22s ease,
    opacity 0.22s ease;
}

.login-button:hover:not(:disabled) {
  box-shadow: 0 20px 42px rgba(255, 95, 3, 0.32);
}

.login-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.loading-spinner {
  width: 22px;
  height: 22px;

  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;

  animation: spin 0.8s linear infinite;
}

.error-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  padding: 12px 14px;

  color: #991b1b;
  background: #fff1f1;
  border: 1px solid rgba(220, 38, 38, 0.18);
  border-radius: var(--radius-sm);

  font-size: 14px;
  font-weight: 700;
  line-height: 1.45;
  text-align: center;
}

.error-icon {
  flex-shrink: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .login-card {
    padding: 28px 20px;
    border-radius: 24px;
  }

  .login-heading h1 {
    font-size: 25px;
  }
}
</style>